import React, { useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material'
import { useMutation } from '@apollo/client'
import { PlayArrow, CheckCircle, Cancel } from '@mui/icons-material'

import { BULK_UPDATE_EXTERNAL_WORK_ITEMS } from './mutations'
import UserSelector from 'src/components/UserSelector'
import WorkItemStatusChip from 'src/components/WorkItemStatusChip'

interface WorkItem {
  id: string
  title: string
  status: string
  assigneeId?: string
  assignee?: {
    firstName: string
    lastName: string
  }
}

interface WorkItemBulkOperationsProps {
  selectedWorkItems: WorkItem[]
  onSuccess: () => void
  open: boolean
  onClose: () => void
}

const WorkItemBulkOperations: React.FC<WorkItemBulkOperationsProps> = ({
  selectedWorkItems,
  onSuccess,
  open,
  onClose,
}) => {
  const [formData, setFormData] = useState({
    status: '',
    assigneeId: '',
    notes: '',
  })

  const [bulkUpdate, { loading }] = useMutation(
    BULK_UPDATE_EXTERNAL_WORK_ITEMS,
    {
      onCompleted: () => {
        onSuccess()
        onClose()
        setFormData({ status: '', assigneeId: '', notes: '' })
      },
      onError: (error) => {
        alert('Failed to update work items: ' + error.message)
      },
    }
  )

  const handleSubmit = () => {
    if (!formData.status && !formData.assigneeId && !formData.notes) {
      alert('Please select at least one field to update')
      return
    }

    const updateData: any = {}
    if (formData.status) updateData.status = formData.status
    if (formData.assigneeId) updateData.assigneeId = formData.assigneeId
    if (formData.notes) updateData.notes = formData.notes

    bulkUpdate({
      variables: {
        input: {
          ids: selectedWorkItems.map((item) => item.id),
          ...updateData,
        },
      },
    })
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'IN_PROGRESS':
        return <PlayArrow fontSize="small" />
      case 'COMPLETED':
        return <CheckCircle fontSize="small" />
      case 'CANCELLED':
        return <Cancel fontSize="small" />
      default:
        return null
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'IN_PROGRESS':
        return 'Start'
      case 'COMPLETED':
        return 'Complete'
      case 'CANCELLED':
        return 'Cancel'
      default:
        return 'Update'
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Bulk Update Work Items ({selectedWorkItems.length} selected)
      </DialogTitle>
      <DialogContent>
        <Box mb={3}>
          <Typography variant="subtitle2" gutterBottom>
            Selected Work Items:
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {selectedWorkItems.map((item) => (
              <Chip
                key={item.id}
                label={item.title}
                size="small"
                variant="outlined"
                icon={getStatusIcon(item.status)}
              />
            ))}
          </Box>
        </Box>

        <Box display="flex" flexDirection="column" gap={2}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              label="Status"
              onChange={(e) =>
                setFormData({ ...formData, status: e.target.value })
              }
            >
              <MenuItem value="">
                <em>No change</em>
              </MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
              <MenuItem value="COMPLETED">Completed</MenuItem>
              <MenuItem value="CANCELLED">Cancelled</MenuItem>
            </Select>
          </FormControl>

          <UserSelector
            value={formData.assigneeId}
            onChange={(assigneeId) => setFormData({ ...formData, assigneeId })}
            label="Assignee"
          />

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes (optional)"
            value={formData.notes}
            onChange={(e) =>
              setFormData({ ...formData, notes: e.target.value })
            }
            placeholder="Add notes for this bulk update..."
          />
        </Box>

        {formData.status && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              This will update the status of {selectedWorkItems.length} work
              item(s) to{' '}
              <WorkItemStatusChip status={formData.status} size="small" />
            </Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            loading ||
            (!formData.status && !formData.assigneeId && !formData.notes)
          }
          startIcon={
            loading ? (
              <CircularProgress size={20} />
            ) : (
              getStatusIcon(formData.status)
            )
          }
        >
          {loading
            ? 'Updating...'
            : getStatusLabel(formData.status) || 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default WorkItemBulkOperations
